import { method } from './method'

/**
 * The live automation diagram on Home: a client's
 * journey from enquiry to a running system, drawn stage by stage as the page scrolls.
 *
 * Coordinates are in the diagram's own 1200 x 600 units: the main path is the top row, the call's
 * outcomes the bottom row. `plane` is who moves it: `lead` = the lead / you, `auto` = the system sends
 * it. `step` is the method step (0, 1 or 2: the three steps in method.ts). Nodes are listed in build order.
 */
export type FlowPlane = 'lead' | 'auto'
export type FlowIcon = 'form' | 'email' | 'booked' | 'reminder' | 'alarm' | 'call' | 'proposal' | 'won' | 'later' | 'nurture' | 'lost'

export type FlowNode = {
  id: string
  title: string
  tag: string
  plane: FlowPlane
  icon: FlowIcon
  x: number
  y: number
  w: number
  h: number
  step: number
  /** One line shown when the stage is inspected. */
  about: string
}

/** `solid` links are the lead path (arrows a packet runs); `dashed` links reroute it: a reschedule loops back, the call branches out. */
export type FlowLink = { from: string; to: string; style: 'solid' | 'dashed'; label?: string }

const ROWS = [64, 376]
const box = (col: number, row: number) => ({ x: 24 + col * 200, y: ROWS[row]!, w: 156, h: 136 })

export const liveAutomation = {
  eyebrow: 'Live automation',
  titleThin: 'Map. Build.',
  titleBold: 'Run.',
  steps: method.steps.map((step) => ({ name: step.name, body: step.body })),
  panel: { lead: 'Enquiry to', rest: 'Running system' },
  legend: { auto: 'Automation', lead: 'Lead path' },
  zones: { booking: 'Your Tools', pipeline: 'Pipeline' },
  connected: 'System live',
  hint: { hover: 'Hover a stage to inspect', tap: 'Tap a stage to inspect' },
  nodes: [
    { id: 'form', title: 'New Enquiry', tag: 'New lead trigger', plane: 'lead', icon: 'form', ...box(0, 0), step: 0, about: 'A lead reaches you through a form, an email or an ad.' },
    { id: 'email', title: 'Instant Reply', tag: 'Sent automatically', plane: 'auto', icon: 'email', ...box(1, 0), step: 1, about: 'The system answers, qualifies and offers a discovery call.' },
    { id: 'booked', title: 'Call Booked', tag: 'Slot confirmed', plane: 'lead', icon: 'booked', ...box(2, 0), step: 1, about: 'The client picks a time without any back-and-forth.' },
    { id: 'day', title: 'Reminder One', tag: 'Pre-call message', plane: 'auto', icon: 'reminder', ...box(3, 0), step: 1, about: 'A reminder goes out the day before the call.' },
    { id: 'hour', title: 'Reminder Two', tag: 'Final nudge', plane: 'auto', icon: 'alarm', ...box(4, 0), step: 1, about: 'A last nudge with a one-click rebook link if they missed it.' },
    { id: 'call', title: 'Discovery Call', tag: 'Map the work', plane: 'lead', icon: 'call', ...box(5, 0), step: 2, about: 'I map the bottlenecks and the steps worth automating.' },
    { id: 'proposal', title: 'Build & Launch', tag: 'Scope + build', plane: 'lead', icon: 'proposal', ...box(0, 1), step: 2, about: 'The approved workflow is built, tested and switched on.' },
    { id: 'later', title: 'Needs a Pass', tag: 'Revisit later', plane: 'lead', icon: 'later', ...box(3, 1), step: 2, about: 'Not ready now — the workflow keeps nurturing them.' },
    { id: 'lost', title: 'Not a Fit', tag: 'Closed out', plane: 'lead', icon: 'lost', ...box(5, 1), step: 2, about: 'A no is closed out cleanly, no follow-up spam.' },
    { id: 'won', title: 'System Live', tag: 'Running', plane: 'lead', icon: 'won', ...box(1, 1), step: 2, about: 'The automation runs and the team gets its hours back.' },
    { id: 'nurture', title: 'Stay in Touch', tag: 'Long-term drip', plane: 'auto', icon: 'nurture', ...box(4, 1), step: 2, about: 'A light drip keeps the door open for later.' },
  ] satisfies FlowNode[],
  links: [
    { from: 'form', to: 'email', style: 'solid' },
    { from: 'email', to: 'booked', style: 'solid' },
    { from: 'booked', to: 'day', style: 'solid' },
    { from: 'day', to: 'hour', style: 'solid' },
    { from: 'hour', to: 'call', style: 'solid' },
    { from: 'hour', to: 'booked', style: 'dashed', label: 'Call rescheduled' },
    { from: 'call', to: 'proposal', style: 'dashed' },
    { from: 'call', to: 'later', style: 'dashed' },
    { from: 'call', to: 'lost', style: 'dashed' },
    { from: 'proposal', to: 'won', style: 'solid' },
    { from: 'later', to: 'nurture', style: 'solid' },
  ] satisfies FlowLink[],
}
